import { Component, DestroyRef, inject, NgZone, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SearchInputComponent } from '../../../../../../components/inputs/search-input/search-input.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../../../../../components/inputs/status-filter/status-filter.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { FeedbackCardComponent } from '../../../../../../components/toasts-and-cards/feedback-card/feedback-card.component';
import { PaginationComponent } from '../../../../../../components/inputs/pagination/pagination.component';
import { MessageService } from '../../../../../../services/message/message.service';
import { LiveRefresh } from '../../../../../../services/live/live-refresh';
import { sendMoment } from '../../../../../../../utils/sms';
import { PromptCardComponent } from '../../../../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import { IPagination } from '../../../../../../types/contactModule';
import { EmptyStateComponent } from '../../../../../../components/ui/empty-state/empty-state.component';
import { StatusComponent } from '../../../../../../components/ui/status/status.component';
import { DataTableComponent } from '../../../../../../components/ui/data-table/data-table.component';
import { SmsTextComponent } from '../../../../../../components/messages/sms-text/sms-text.component';
import { DeliveryComponent } from '../../../../../../components/messages/delivery/delivery.component';
import { MessageRow, toMessageRows } from './message-row';

type TStatusSort = 'all' | 'completed' | 'pending';
type TArrangementSort =
  | 'messageSubject,asc'
  | 'messageSubject,desc'
  | 'createdAt,desc'
  | 'createdAt,asc'
  | 'modifiedAt,desc'
  | 'modifiedAt,asc';

/** Refresh this often while a message is going out or due within two minutes… */
const LIVE_FAST_MS = 10_000;
/** …and this often otherwise. */
const LIVE_CALM_MS = 30_000;
const DUE_SOON_MS = 2 * 60_000;

@Component({
  selector: 'active-messages-table',
  standalone: true,
  imports: [
    SearchInputComponent,
    MatTooltipModule,
    PromptCardComponent,
    StatusFilterComponent,
    SortDropdownComponent,
    FeedbackCardComponent,
    PaginationComponent,
    EmptyStateComponent,
    StatusComponent,
    DataTableComponent,
    SmsTextComponent,
    DeliveryComponent,
    RouterLink,
  ],
  templateUrl: './active-messages-table.component.html',
  styleUrls: ['./message-table.css', './active-messages-table.component.css'],
})
export class ActiveMessagesTableComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private zone = inject(NgZone);
  private searchSubject = new Subject<string>();

  router = inject(Router);
  messageService = inject(MessageService);

  messages: any[] = [];
  /** Display values for each message, worked out once per load. */
  rows: MessageRow[] = [];
  /** Rows that arrived with a background refresh; they slide in once. */
  freshKeys = new Set<string | number>();
  selectedMessage: any = {};
  showArchiveModal: boolean = false;
  statusSort: TStatusSort = 'all';
  arrangementSort: TArrangementSort = 'createdAt,desc';

  readonly statusOptions: StatusFilterOption[] = [
    { value: 'all', title: 'All', icon: 'list', tone: 'neutral' },
    { value: 'pending', title: 'Pending', icon: 'schedule', tone: 'warning' },
    {
      value: 'completed',
      title: 'Completed',
      icon: 'check_circle',
      tone: 'success',
    },
  ];

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'messageSubject',
    nameLabel: 'Subject',
    createdField: 'createdAt',
    modifiedField: 'modifiedAt',
  });
  showCancelModal: boolean = false;
  messagePage: number = 0;
  pageSize: number = 10;
  searchTerm: string = '';
  paginationData: IPagination = {
    totalElements: 0,
    pageSize: this.pageSize,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  };
  params: Record<string, unknown> = {
    'archive.specified': false,
  };

  loading: boolean = false;

  get emptyMessage(): string {
    if (this.searchTerm.trim()) return `No messages match “${this.searchTerm.trim()}”.`;
    if (this.statusSort === 'pending') return 'Nothing scheduled.';
    if (this.statusSort === 'completed') return 'No completed messages yet.';
    return 'No active messages yet.';
  }

  openArchiveModal(selectedMessage: any) {
    this.selectedMessage = selectedMessage;
    this.showArchiveModal = true;
  }

  closeArchiveModal() {
    this.showArchiveModal = false;
  }

  handleArchiveMessage() {
    this.messageService
      .updateMessageStatus({
        id: this.selectedMessage.id,
        uuid: this.selectedMessage.uuid,
        status: 'Completed',
        archive: true,
      })
      .subscribe({
        next: () => {
          this.messageService.fetchMessages(this.updatedParams);
        },
        error: () => {},
        complete: () => {
          this.closeArchiveModal();
        },
      });
  }

  openCancelModal(selectedMessage: any) {
    this.selectedMessage = selectedMessage;
    this.showCancelModal = true;
  }

  closeCancelModal() {
    this.showCancelModal = false;
  }

  handleCancelMessage() {
    this.messageService
      .updateMessageStatus({
        id: this.selectedMessage.id,
        uuid: this.selectedMessage.uuid,
        status: 'Canceled',
      })
      .subscribe({
        next: () => {
          this.messageService.fetchMessages(this.updatedParams);
        },
        error: () => {},
        complete: () => {
          this.closeCancelModal();
        },
      });
  }

  updateMessagesPage(newPage: number) {
    this.messagePage = newPage;
    this.messageService.fetchMessages(this.updatedParams);
  }

  updateSortCriteria(criteria: TStatusSort) {
    this.statusSort = criteria;
    switch (criteria) {
      case 'all':
        this.params['status.in'] = ['Completed', 'Pending', 'Active'];
        break;
      case 'pending':
        this.params['status.in'] = ['Pending'];
        break;
      case 'completed':
        this.params['status.in'] = ['Completed'];
        break;
      default:
        this.params['status.in'] = ['Completed', 'Pending', 'Active'];
        break;
    }
    // A new filter is a new list: start from its first page.
    this.messagePage = 0;
    this.messageService.fetchMessages(this.updatedParams);
  }

  updateSortArrangementCriteria(criteria: TArrangementSort) {
    this.arrangementSort = criteria;
    this.params['sort'] = criteria;
    this.messagePage = 0;
    this.messageService.fetchMessages(this.updatedParams);
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    this.messagePage = 0;
    if (term.length < 1) {
      delete this.params['messageSubject.contains'];
    } else {
      this.params['messageSubject.contains'] = term;
    }
    this.searchSubject.next(term);
  }

  get updatedParams() {
    // Spring Pageable reads `size`; `pageSize` is ignored (it always sent 20 rows).
    return { ...this.params, page: this.messagePage, size: this.pageSize };
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.messagePage = 0;
    this.messageService.fetchMessages(this.updatedParams);
  }

  goToMessageDetails(message: any) {
    this.messageService.updateSelectedMessage(message);
    this.router.navigate(['/organization/messages', message?.uuid]);
  }

  /**
   * The subject is the row's link: it navigates itself (and opens a new tab
   * on Cmd/Ctrl-click), so the row's own click handler stays out of it.
   */
  selectFromLink(event: Event, message: any) {
    event.stopPropagation();
    this.messageService.updateSelectedMessage(message);
  }

  goToMessageReport(message: any) {
    this.messageService.updateSelectedMessage(message);
    this.router.navigate(['/organization/messages/report', message?.uuid]);
  }

  /** A message on this page is sending, or scheduled to go within two minutes. */
  private somethingGoingOut(): boolean {
    const soon = Date.now() + DUE_SOON_MS;
    return this.rows.some((row) => {
      if (row.status === 'active') return true;
      if (row.status !== 'pending') return false;
      const at = sendMoment(row.message)?.getTime();
      return at !== undefined && at <= soon;
    });
  }

  /** The dashboard links here with ?status=pending; open on that filter. */
  private applyStatusFromUrl() {
    const status = this.route.snapshot.queryParamMap.get('status')?.toLowerCase();
    if (status === 'pending' || status === 'completed') {
      this.statusSort = status;
      this.params['status.in'] = [status === 'pending' ? 'Pending' : 'Completed'];
    }
  }

  ngOnInit() {
    this.params['status.in'] = ['Completed', 'Pending', 'Active'];
    this.params['archive.notEquals'] = true;
    this.params['sort'] = 'createdAt,desc';
    this.applyStatusFromUrl();
    this.messageService.fetchMessages(this.updatedParams);

    this.searchSubject
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.messageService.fetchMessages(this.updatedParams));

    // New sends appear and statuses move on their own, with the same filters,
    // search, sort and page. The delivery column refreshes separately.
    const live = new LiveRefresh(this.zone, {
      refresh: () => this.messageService.fetchMessages(this.updatedParams, { silent: true }),
      interval: () => (this.somethingGoingOut() ? LIVE_FAST_MS : LIVE_CALM_MS),
    });
    live.start();
    this.destroyRef.onDestroy(() => live.stop());

    this.messageService.getMessages
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((messages: any[]) => {
        const shown = new Set(this.rows.map((row) => row.key));
        this.messages = messages ?? [];
        this.rows = toMessageRows(this.messages);
        this.freshKeys =
          this.messageService.lastMessagesSilent && shown.size
            ? new Set(this.rows.filter((row) => !shown.has(row.key)).map((row) => row.key))
            : new Set();
        // A send in view speeds the refresh up straight away.
        live.nudge();
      });

    this.messageService.getActiveMessagesPagination
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: IPagination) => {
        this.paginationData = value;
      });

    this.messageService.getLoadingActiveMessages
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => {
        this.loading = loading;
      });
  }
}
